import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Shield, Eye, EyeOff, Loader2, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

export default function LoginPage() {
    const { login, requires2FA, setRequires2FA } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [loginData, setLoginData] = useState({ username: '', password: '' });
    const [twoFaCode, setTwoFaCode] = useState('');
    const [show2FAInput, setShow2FAInput] = useState(false);
    
    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        
        try {
            const result = await login(
                loginData.username, 
                loginData.password,
                show2FAInput ? twoFaCode : null
            );
            
            if (result?.requires2FA) {
                setShow2FAInput(true);
                toast.info('Please enter your 2FA code');
            } else {
                toast.success('Welcome back!');
            }
        } catch (error) {
            const message = error.response?.data?.detail || 'Login failed';
            toast.error(message);
            
            // If 2FA code was wrong, don't reset the form
            if (!message.includes('2FA')) {
                setShow2FAInput(false);
                setTwoFaCode('');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleBack = () => {
        setShow2FAInput(false);
        setTwoFaCode('');
        setRequires2FA(false);
    };
    
    return (
        <div className="min-h-screen flex flex-col lg:flex-row">
            {/* Hero Section */}
            <div className="lg:flex-1 bg-secondary relative overflow-hidden">
                <div 
                    className="absolute inset-0 bg-cover bg-center opacity-30"
                    style={{ 
                        backgroundImage: 'url(https://images.unsplash.com/photo-1769447193698-ef5283f3afec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxTRVMlMjB2b2x1bnRlZXIlMjBlbWVyZ2VuY3klMjByZXNjdWUlMjBmbG9vZCUyMHN0b3JtfGVufDB8fHx8MTc3Mzc1NTE5NXww&ixlib=rb-4.1.0&q=85)' 
                    }}
                />
                <div className="relative z-10 flex flex-col h-full p-8 lg:p-16 text-white min-h-[300px] lg:min-h-screen">
                    {/* Logo at top, centered */}
                    <div className="flex justify-center pt-4 lg:pt-8 mb-8">
                        <img 
                            src="/react-qld-logo.png" 
                            alt="REACT QLD Logo" 
                            className="h-36 lg:h-52 object-contain"
                        />
                    </div>
                    
                    {/* Content centered vertically in remaining space */}
                    <div className="flex-1 flex flex-col justify-center items-center">
                        <div className="max-w-md text-center">
                            <h1 className="text-3xl lg:text-5xl font-bold mb-4">
                                Response,<br />
                                Engagement &<br />
                                Attendance<br />
                                <span className="text-primary">Coordination Tool</span>
                            </h1>
                            <p className="text-lg text-gray-300 mb-8 hidden lg:block">
                                Response Management System for Emergency Service & Response volunteers. 
                                Manage your team, track attendance, and stay prepared.
                            </p>
                            <div className="hidden lg:flex items-center justify-center gap-6 text-sm text-gray-400">
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                    <span>Real-time tracking</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-primary"></div>
                                    <span>Mobile ready</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Auth Section */}
            <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-background">
                <Card className="w-full max-w-md border-0 shadow-xl">
                    <CardHeader className="text-center pb-2">
                        {show2FAInput ? (
                            <>
                                <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                                    <KeyRound className="w-6 h-6 text-primary" />
                                </div>
                                <CardTitle className="text-2xl">Two-Factor Authentication</CardTitle>
                                <CardDescription>
                                    Enter the 6-digit code from your authenticator app
                                </CardDescription>
                            </>
                        ) : (
                            <>
                                <CardTitle className="text-2xl">Welcome</CardTitle>
                                <CardDescription>
                                    Sign in to access the Response Management System
                                </CardDescription>
                            </>
                        )}
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleLogin} className="space-y-4">
                            {!show2FAInput ? (
                                <>
                                    <div className="space-y-2">
                                        <Label htmlFor="login-username">Username</Label>
                                        <Input
                                            id="login-username"
                                            data-testid="login-username"
                                            placeholder="Enter your username"
                                            value={loginData.username}
                                            onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                                            required
                                            disabled={isLoading}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="login-password">Password</Label>
                                        <div className="relative">
                                            <Input
                                                id="login-password"
                                                data-testid="login-password"
                                                type={showPassword ? 'text' : 'password'}
                                                placeholder="Enter your password"
                                                value={loginData.password}
                                                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                                                required
                                                disabled={isLoading}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                            >
                                                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="space-y-2">
                                    <Label htmlFor="2fa-code">Authentication Code</Label>
                                    <Input
                                        id="2fa-code"
                                        data-testid="2fa-code"
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        maxLength={6}
                                        placeholder="000000"
                                        value={twoFaCode}
                                        onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                        required
                                        disabled={isLoading}
                                        className="text-center text-2xl tracking-widest font-mono"
                                        autoFocus
                                    />
                                    <p className="text-xs text-muted-foreground text-center">
                                        Open your authenticator app to view your code
                                    </p>
                                </div>
                            )}
                            
                            <Button 
                                type="submit" 
                                className="w-full ses-btn-primary h-11"
                                disabled={isLoading || (show2FAInput && twoFaCode.length !== 6)}
                                data-testid="login-submit"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        {show2FAInput ? 'Verifying...' : 'Signing in...'}
                                    </>
                                ) : (
                                    show2FAInput ? 'Verify Code' : 'Sign In'
                                )}
                            </Button>

                            {show2FAInput && (
                                <Button 
                                    type="button"
                                    variant="ghost"
                                    className="w-full"
                                    onClick={handleBack}
                                    disabled={isLoading}
                                >
                                    Back to Login
                                </Button>
                            )}
                        </form>
                        
                        <div className="mt-6 pt-6 border-t text-center">
                            <p className="text-sm text-muted-foreground">
                                Don't have an account? Contact your unit administrator to request access.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
